import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon, AppsIcon, UserMultipleIcon, LinkIcon, CalendarIcon, ToggleOffIcon, InformationCircleMiniIcon } from '@calendly/ui/components/icons';
import { InlineBanner } from '@calendly/ui/components/inline-banner';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextAction, colorTextDestructive } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useState } from 'react';
import { EventTypeSelect } from 'features/meeting_intelligence/components/event_type_select';
import { useNotetakerSettingsContext } from 'features/meeting_intelligence/settings/notetaker_settings_provider';
import { useMeetingIntelligenceContext } from 'features/meeting_intelligence/settings/provider';
import { DropdownWrapper, DropdownErrorWrapper, TitleWrapper } from './styles';
const DROPDOWN_OPTIONS = [{
  value: 'ALL',
  icon: AppsIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.all'
}, {
  value: 'OUTSIDE_COMPANY',
  icon: UserMultipleIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.outside_company'
}, {
  value: 'VIA_CALENDLY',
  icon: LinkIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.via_calendly'
}, {
  value: 'VIA_CALENDAR',
  icon: CalendarIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.via_calendar'
}, {
  value: 'MANUAL',
  icon: ToggleOffIcon,
  labelKey: 'publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.manual',
  dividerAbove: true
}];
const inlineDropdownTextCss = "igc4xvu";
const InlineDropdownText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(inlineDropdownTextCss, props.className)} />;
});
const dropdownContainerCss = "doy6yto";
const DropdownContainer = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(dropdownContainerCss, props.className)} />);
const textContainerCss = "tkvpidb";
const TextContainer = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(textContainerCss, props.className)} />);
const bareButtonCss = "b1hivdip";
const StyledBareButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(bareButtonCss, props.className)} />);
const eventTypeDropdownSpacingCss = "ewfqo4t";
const manualMenuItemCss = "m1exdwzm";
const recordingInfoContainerCss = "rnqcyze";
const truncatedEmailCss = "tv9m1d7";
const RecordingInfoContainer = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(recordingInfoContainerCss, props.className)} />);
const dropdownMenuItemCss = "d1asfjxw";
const DropdownMenuItemContent = ({
  icon: Icon,
  label
}) => <span className={dropdownMenuItemCss}>
    {Icon && <Icon />}
    {label}
  </span>;
export const RecordingDefaultsBody = () => {
  const {
    recordingDefaultOption,
    setRecordingDefaultOption,
    eventTypes,
    hasEventError,
    actualSelectedCount,
    handleEventTypeSelect,
    handleSelectAll,
    handleClearAll,
    handleFetchMoreEvents
  } = useNotetakerSettingsContext();
  const {
    email,
    isValidCalendar
  } = useMeetingIntelligenceContext() || {};
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const calendarEmail = email || I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.dropdown_options.via_calendar_default');
  const allInfoCalendarEmail = email || I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.all_info_default');
  const selectedOption = DROPDOWN_OPTIONS.find(opt => opt.value === recordingDefaultOption);
  const dropdownOptionLabel = selectedOption ? I18n.t(selectedOption.labelKey, selectedOption.value === 'VIA_CALENDAR' ? {
    calendar_email: calendarEmail
  } : undefined) : '';
  const visibleOptions = DROPDOWN_OPTIONS.filter(option => option.value !== 'VIA_CALENDAR' || isValidCalendar);
  const optionsBeforeDivider = visibleOptions.filter(option => !option.dividerAbove);
  const optionsAfterDivider = visibleOptions.filter(option => option.dividerAbove);
  const handleOptionSelect = value => {
    setRecordingDefaultOption(value);
    setIsDropdownOpen(false);
  };
  const handleToggleDropdown = () => {
    setIsDropdownOpen(open => !open);
  };
  return <div>
      <TitleWrapper>
        <BodyText size="medium" weight="bold">
          {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.title')}
        </BodyText>
        <BodyText size="medium" color="muted">
          {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.description')}
        </BodyText>
      </TitleWrapper>

      <TextContainer>
        {recordingDefaultOption === 'ERROR' ? <InlineBanner title={I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.update_settings_banner.title')} variant="neutral" isDismissible={false} message={I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.update_settings_banner.message')} primaryButton={{
        decoration: 'secondary-outline',
        label: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.update_settings_banner.button_label'),
        onClick: () => setRecordingDefaultOption('VIA_CALENDLY')
      }} /> : <>
            <BodyText size="medium" as="span">
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.notetaker_should_join')}
              {' '}
            </BodyText>
            <DropdownContainer>
              <DropdownMenu isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)}>
                <DropdownMenuTrigger>
                  <StyledBareButton onClick={handleToggleDropdown} aria-expanded={isDropdownOpen} aria-haspopup="menu">
                    <InlineDropdownText>
                      {dropdownOptionLabel}
                    </InlineDropdownText>
                    {isDropdownOpen ? <ArrowChevronUpMiniIcon color={colorTextAction} /> : <ArrowChevronDownMiniIcon color={colorTextAction} />}
                  </StyledBareButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent placement="bottom start" data-testid="recording-defaults-menu">
                  <DropdownMenuGroup>
                    {optionsBeforeDivider.map(option => <DropdownMenuItem key={option.value} onSelect={() => handleOptionSelect(option.value)}>
                        <DropdownMenuItemContent icon={option.icon} label={I18n.t(option.labelKey, option.value === 'VIA_CALENDAR' ? {
                    calendar_email: calendarEmail
                  } : undefined)} />
                      </DropdownMenuItem>)}
                  </DropdownMenuGroup>
                  {optionsAfterDivider.length > 0 && <DropdownMenuGroup>
                      {optionsAfterDivider.map(option => <DropdownMenuItem key={option.value} className={manualMenuItemCss} onSelect={() => handleOptionSelect(option.value)}>
                          <DropdownMenuItemContent icon={option.icon} label={I18n.t(option.labelKey)} />
                        </DropdownMenuItem>)}
                    </DropdownMenuGroup>}
                </DropdownMenuContent>
              </DropdownMenu>
            </DropdownContainer>
          </>}
      </TextContainer>
      {recordingDefaultOption === 'ALL' && isValidCalendar && <RecordingInfoContainer>
        <InformationCircleMiniIcon />
        <BodyText size="small">
          {email ? <>
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.all_info_prefix')}
              {' '}
              <span className={truncatedEmailCss} title={email}>{email}</span>
              .
            </> : I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.all_info', {
          calendar_email: allInfoCalendarEmail
        })}
        </BodyText>
      </RecordingInfoContainer>}
      {recordingDefaultOption === 'OUTSIDE_COMPANY' && <RecordingInfoContainer>
          <InformationCircleMiniIcon />
          <BodyText size="small">
            {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.outside_company_info')}
          </BodyText>
        </RecordingInfoContainer>}
      {recordingDefaultOption === 'VIA_CALENDLY' && <div>
          <div className={eventTypeDropdownSpacingCss}>
            <DropdownWrapper>
              <EventTypeSelect placeholder={I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults.no_event_types_placeholder')} disabled={false} eventTypes={eventTypes} onChange={handleEventTypeSelect} handleSelectAll={handleSelectAll} handleClearAll={handleClearAll} handleFetchMoreEvents={handleFetchMoreEvents} actualSelectedCount={actualSelectedCount} isWide />
              {hasEventError && <DropdownErrorWrapper>
                  <BodyText color={colorTextDestructive} size="medium">
                    {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.dropdown_error_text')}
                  </BodyText>
                </DropdownErrorWrapper>}
            </DropdownWrapper>
          </div>
        </div>}
    </div>;
};

require("./recording_defaults_body.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./recording_defaults_body.js");